/**
 * Phase 5 — Agent hardware abstraction.
 *
 * The same React codebase ships in two shells:
 *
 *   * The browser (customer / merchant / admin / agent dashboards on
 *     pay.mojomoney.africa).
 *   * The Capacitor wrapper at agent/ (Android APK installed on agent
 *     terminals, e.g. the Topwise T1N fleet).
 *
 * Native-only capabilities (fingerprint reader, GPS, ESC/POS printer)
 * are exposed through the abstractions below. When the browser context
 * doesn't support them, we polyfill with a deterministic mock so:
 *
 *   * Playwright can drive the grant-payout flow end-to-end without
 *     touching native hardware.
 *   * A developer on a laptop can hit /agent/grant-payout, see the form,
 *     and walk through the wizard logically before they have a device.
 *
 * The browser polyfills are NEVER used for real grants — the backend
 * /api/grants/payout requires biometric_confidence above
 * agent_biometric_min_confidence and gps_accuracy below
 * agent_gps_max_accuracy_m. The polyfills surface
 * confidence=0.05 / accuracy_m=9999 so the server rejects them.
 *
 * Capacitor plugin contract
 * =========================
 * When the app is built against Capacitor (window.Capacitor exists +
 * isNativePlatform()), we look up plugins on window.Capacitor.Plugins:
 *
 *   BiometricAuth.captureFingerprint() -> {hash, method, confidence}
 *   Geolocation.getCurrentPosition()   -> {coords:{lat,lng,accuracy}}
 *   EscPosPrinter.print({text})        -> {ok}
 *
 * The actual plugin class names + signatures are placeholders; the
 * Capacitor build wires them at agent/ build time. This file only
 * cares that the contract matches.
 */

const POLYFILL_LATENCY_MS = 250;

/** True when running inside Capacitor (the agent shell). */
export function isNativeAgentApp() {
  try {
    return !!(typeof window !== 'undefined'
              && window.Capacitor
              && typeof window.Capacitor.isNativePlatform === 'function'
              && window.Capacitor.isNativePlatform());
  } catch (_) {
    return false;
  }
}

function _capacitorPlugin(name) {
  try {
    return window?.Capacitor?.Plugins?.[name] || null;
  } catch (_) {
    return null;
  }
}

// ─── Fingerprint ──────────────────────────────────────────────────────

/**
 * Capture a fingerprint sample. Returns { hash, method, confidence }.
 *
 *   hash       — SHA-256 hex of the (one-way-hashed) template the
 *                plugin reads from the reader. The server stores this;
 *                we never see the template itself.
 *   method     — 'fingerprint' (current), reserved for future:
 *                'face', 'iris'.
 *   confidence — 0..1, the device's reported match confidence.
 *
 * Browser polyfill uses a low-confidence synthetic hash so the server
 * rejects it (the backend min_confidence default is 0.70).
 */
export async function captureFingerprint() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('BiometricAuth');
    if (plug && typeof plug.captureFingerprint === 'function') {
      const out = await plug.captureFingerprint();
      // Plugin may return a Capacitor-friendly shape; normalise here.
      return {
        hash:       String(out.hash || ''),
        method:     String(out.method || 'fingerprint'),
        confidence: Number(out.confidence || 0),
      };
    }
    throw new Error('BiometricAuth plugin is not installed on this device');
  }
  // Browser polyfill — deterministic low-confidence hash. The backend
  // will reject it (confidence < 0.70 by default) so this never enables
  // a real grant payout from a laptop browser.
  await _sleep(POLYFILL_LATENCY_MS);
  const hash = await _sha256Hex(`mojo-mock-fp-${Date.now()}`);
  return { hash, method: 'fingerprint', confidence: 0.05 };
}

// ─── GPS ──────────────────────────────────────────────────────────────

/**
 * Get current location. Returns { latitude, longitude, accuracy_m }.
 *
 * Browser polyfill returns a deliberately wide accuracy (9999m) so the
 * backend GPS-accuracy check rejects it. We also fall back to navigator
 * .geolocation if the user has granted it (e.g. tests that want a real
 * coord), then clamp accuracy to a similar veto value.
 */
export async function getCurrentLocation() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('Geolocation');
    if (plug && typeof plug.getCurrentPosition === 'function') {
      const out = await plug.getCurrentPosition({ enableHighAccuracy: true });
      const c = out.coords || out;
      return {
        latitude:   Number(c.latitude),
        longitude:  Number(c.longitude),
        accuracy_m: Number(c.accuracy ?? c.accuracy_m ?? 0),
      };
    }
    throw new Error('Geolocation plugin is not installed on this device');
  }
  // Browser polyfill — try navigator.geolocation; clamp accuracy_m so the
  // server rejects mock readings.
  if (typeof navigator !== 'undefined' && navigator.geolocation) {
    try {
      const pos = await new Promise((res, rej) => {
        navigator.geolocation.getCurrentPosition(res, rej,
          { enableHighAccuracy: false, timeout: 4000 });
      });
      return {
        latitude:   Number(pos.coords.latitude),
        longitude:  Number(pos.coords.longitude),
        // Force veto value — see comment at the top of the file.
        accuracy_m: 9999,
      };
    } catch (_) { /* fall through to synthetic */ }
  }
  return { latitude: 0, longitude: 0, accuracy_m: 9999 };
}

// ─── Printer ──────────────────────────────────────────────────────────

/**
 * Print a receipt body. The native printer takes a plain-text block and
 * cuts the paper at the end; the browser polyfill opens window.print()
 * with the receipt preformatted, so a developer can verify layout.
 */
export async function printReceipt(text) {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('EscPosPrinter');
    if (plug && typeof plug.print === 'function') {
      await plug.print({ text, cut: true });
      return { ok: true };
    }
    throw new Error('EscPosPrinter plugin is not installed on this device');
  }
  if (typeof window !== 'undefined' && typeof window.print === 'function') {
    // Open a popup with just the receipt text in <pre> + invoke print.
    const w = window.open('', '_blank', 'noopener,width=400,height=600');
    if (!w) return { ok: false, reason: 'popup_blocked' };
    w.document.write(`<!doctype html><html><head><title>Mojo Grant Receipt</title>
      <style>body{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;white-space:pre-wrap;padding:16px;}</style>
      </head><body><pre>${_escapeHtml(text)}</pre></body></html>`);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 50);
    return { ok: true };
  }
  return { ok: false, reason: 'no_printer' };
}

// ─── Barcode scanner (Phase 5a gap-close) ─────────────────────────────

/**
 * Scan a barcode (1D / 2D / QR). Returns { content, format } on success.
 * Capacitor plugin: MLKitBarcodeScanner. Browser polyfill: NOT supported —
 * raises so the page can show a "use the agent terminal" hint.
 */
export async function scanBarcode({ formats = ['QR_CODE', 'CODE_128', 'PDF_417'] } = {}) {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('MLKitBarcodeScanner') || _capacitorPlugin('BarcodeScanner');
    if (plug && typeof plug.scan === 'function') {
      const out = await plug.scan({ formats });
      const first = (out.barcodes && out.barcodes[0]) || out;
      return {
        content: String(first.rawValue || first.content || first.code || ''),
        format:  String(first.format || ''),
      };
    }
    throw new Error('BarcodeScanner plugin is not installed on this device');
  }
  throw new Error('Barcode scanning is only available on the agent terminal');
}


// ─── Kiosk mode (Phase 5a gap-close) ──────────────────────────────────

/**
 * Lock the device into single-app (Android Lock Task) mode.
 * Capacitor plugin: KioskMode. Requires the device to be enrolled in a
 * DPC (Device Policy Controller); without it, the call no-ops with
 * supported=false.
 *
 * Returns {supported, locked}.
 */
export async function enableKioskMode() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('KioskMode');
    if (plug && typeof plug.start === 'function') {
      const r = await plug.start();
      return { supported: true, locked: !!(r && r.locked) };
    }
  }
  return { supported: false, locked: false };
}

export async function disableKioskMode() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('KioskMode');
    if (plug && typeof plug.stop === 'function') {
      await plug.stop();
      return { supported: true, locked: false };
    }
  }
  return { supported: false, locked: false };
}


// ─── Tamper / root detection (Phase 5a gap-close) ─────────────────────

/**
 * Ask the device whether the runtime looks tampered (rooted, debug build,
 * known emulator). Returns { trust_level, signals } where trust_level is
 * one of 'high' | 'medium' | 'low' | 'browser'. The agent server can
 * refuse a payout on low-trust devices.
 */
export async function getDeviceIntegrity() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('RootDetection')
              || _capacitorPlugin('PlayIntegrity')
              || _capacitorPlugin('DeviceIntegrity');
    if (plug && typeof plug.check === 'function') {
      const r = await plug.check();
      // Plugins vary in shape; coerce.
      const trust =
        r.trust_level
        || (r.rooted ? 'low'
          : r.developerMode || r.emulator ? 'medium'
          : 'high');
      return {
        trust_level: trust,
        signals: r.signals || r.flags || {
          rooted:        !!r.rooted,
          developer:     !!r.developerMode,
          emulator:      !!r.emulator,
        },
      };
    }
    // Plugin not installed on this device — be conservative.
    return { trust_level: 'medium', signals: { plugin_missing: true } };
  }
  // Browser context: we have no signal at all, so 'browser' is its own
  // bucket. The server-side gate (Phase 5a) refuses payouts when
  // trust_level == 'browser' regardless of any other check.
  return { trust_level: 'browser', signals: {} };
}


// ─── Forced-update gate (Phase 5a gap-close) ──────────────────────────

/** Compare two semver-ish strings; returns -1/0/+1. Lenient — defaults
 * missing parts to 0 so 'web-1.0.0' < '1.2.0'. */
function _cmpVersions(a, b) {
  const ax = String(a || '').replace(/^[a-z-]+/i, '').split('.').map((p) => parseInt(p, 10) || 0);
  const bx = String(b || '').replace(/^[a-z-]+/i, '').split('.').map((p) => parseInt(p, 10) || 0);
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const av = ax[i] ?? 0, bv = bx[i] ?? 0;
    if (av < bv) return -1;
    if (av > bv) return  1;
  }
  return 0;
}

/**
 * Check whether the running app meets the minimum version returned by
 * GET /api/agent/config. Returns { ok, hardBlock, currentVersion,
 * minVersion }. The Agent UI surfaces a banner when !ok and a hard
 * block screen when hardBlock.
 *
 * This is a CLIENT-side hint — the server still refuses calls from
 * outdated agents because /api/grants/payout reads
 * `agent_min_app_version_hard_block` and gates on it.
 */
export async function checkForcedUpdate(config) {
  const info = await getDeviceInfo();
  const minV = (config && config.min_app_version) || '1.0.0';
  const cmp = _cmpVersions(info.app_version, minV);
  const ok = cmp >= 0;
  return {
    ok,
    hardBlock: !ok,
    currentVersion: info.app_version,
    minVersion:     minV,
  };
}


// ─── Device id + app version ──────────────────────────────────────────

/**
 * Stable, opaque-ish device identifier. On native we expose Capacitor's
 * Device.getInfo().uuid; on web we fall back to a generated browser id
 * stored in localStorage so re-loads of the dashboard during testing
 * keep the same id.
 */
export async function getDeviceInfo() {
  if (isNativeAgentApp()) {
    const plug = _capacitorPlugin('Device');
    if (plug && typeof plug.getInfo === 'function') {
      const info = await plug.getInfo();
      return {
        device_id:   String(info.uuid || info.identifier || 'unknown'),
        app_version: String(info.appVersion || info.osVersion || '1.0.0'),
      };
    }
  }
  // Web fallback.
  let id = null;
  try { id = localStorage.getItem('mojo-agent-web-device-id'); } catch (_) {}
  if (!id) {
    id = `web-${(typeof crypto !== 'undefined' && crypto.randomUUID
                 ? crypto.randomUUID() : Date.now())}`;
    try { localStorage.setItem('mojo-agent-web-device-id', id); } catch (_) {}
  }
  return { device_id: id, app_version: 'web-1.0.0' };
}

// ─── Helpers ──────────────────────────────────────────────────────────

function _sleep(ms) { return new Promise((res) => setTimeout(res, ms)); }

async function _sha256Hex(text) {
  // Browser SubtleCrypto path; works in modern Chromium + Capacitor WebView ≥67.
  if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
    const data = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(hash);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
  }
  // Last-resort fallback for environments without SubtleCrypto (old WebView
  // versions on the Topwise T1N firmware): a non-cryptographic stub that
  // is still 64 lowercase hex chars so the backend Pydantic validator
  // accepts the shape. We do NOT pretend this is a real SHA-256 hash —
  // the backend's biometric_confidence check is the real gate.
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) - h) + text.charCodeAt(i);
    h |= 0;
  }
  const hex = Math.abs(h).toString(16).padStart(64, '0').slice(0, 64);
  return hex;
}

function _escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
